import { useState, type FormEvent } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.js';
import { ROLES, type Role, CATEGORIES, type Category } from '@sfp/shared/roles';
import { ROLE_DEFAULT_PERMISSIONS } from '@sfp/shared/permissions';
import { DEFAULT_HOME_LAYOUT } from '../../lib/home-defaults.js';

interface InviteResult {
  uid: string;
  email: string;
  passwordResetLink: string;
  alreadyExisted: boolean;
}

/**
 * "Setup shulkinfamilypartners.com Email Address" wizard.
 * Lives at /admin/invite. Admin-only.
 */
export function InviteUser() {
  const { role } = useAuth();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [userRole, setUserRole] = useState<Role>('family_member');
  const [allowedCategories, setAllowedCategories] = useState<Category[]>([]);
  const [allowedTileGroupIds, setAllowedTileGroupIds] = useState<string[]>([]);
  const [allowedSidebarItemIds, setAllowedSidebarItemIds] = useState<string[]>([]);
  const [allowedTopicIds, setAllowedTopicIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<InviteResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (role !== 'admin') {
    return (
      <div className="mx-auto max-w-xl px-6 py-12 text-sm text-stone-500">
        Admins only.
      </div>
    );
  }

  function applyRoleDefaults(r: Role) {
    setUserRole(r);
    const def = ROLE_DEFAULT_PERMISSIONS[r];
    if (def) {
      setAllowedTileGroupIds(def.allowedTileGroupIds);
      setAllowedSidebarItemIds(def.allowedSidebarItemIds);
      setAllowedTopicIds(def.allowedTopicIds);
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const fn = httpsCallable<unknown, InviteResult>(functions, 'inviteUser');
      const r = await fn({
        email: email.trim().toLowerCase(),
        displayName: displayName.trim() || undefined,
        role: userRole,
        allowedTileGroupIds,
        allowedSidebarItemIds,
        allowedTopActionIds: [],
        allowedTopicIds,
      });
      setResult(r.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setBusy(false);
    }
  }

  const TOPICS = [
    { id: 'dropbox', label: 'Dropbox' },
    { id: 'sharepoint', label: 'SharePoint' },
    { id: 'vos', label: 'vOS' },
    { id: 'gmail', label: 'Gmail / Bills inbox' },
    { id: 'plaid', label: 'Plaid' },
  ];

  return (
    <div className="mx-auto max-w-3xl px-6 py-8 space-y-6">
      <header>
        <div className="text-[10px] uppercase tracking-wider text-stone-400">Admin</div>
        <h1 className="text-2xl font-semibold text-stone-800">Setup shulkinfamilypartners.com email address</h1>
        <p className="mt-2 text-sm leading-relaxed text-stone-600">
          Add a new user to SFP. Creates a Firebase Auth account, adds the email to the allowlist (so they
          can actually sign in), sets their role + granular permissions, and produces a password-reset link
          you can hand off so they set their own password on first sign-in.
        </p>
      </header>

      {result ? (
        <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700">Success</div>
          <h2 className="mt-1 text-lg font-semibold text-stone-800">
            {result.alreadyExisted ? 'Permissions updated for ' : 'Invited '}
            {result.email}
          </h2>
          <div className="mt-4 space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-stone-400">UID</div>
              <div className="font-mono text-xs text-stone-600">{result.uid}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-stone-400">
                Password reset link — send this to the user
              </div>
              <textarea
                readOnly
                value={result.passwordResetLink}
                rows={4}
                className="mt-1 w-full rounded border border-stone-300 bg-stone-50 px-3 py-2 font-mono text-xs"
              />
              <button
                type="button"
                onClick={() => navigator.clipboard.writeText(result.passwordResetLink)}
                className="mt-2 rounded bg-[color:var(--sfp-primary)] px-3 py-1 text-xs font-medium text-white"
              >
                Copy link
              </button>
            </div>
            <button
              type="button"
              onClick={() => {
                setResult(null);
                setEmail('');
                setDisplayName('');
              }}
              className="text-xs text-stone-500 hover:text-stone-800"
            >
              Invite another →
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4 rounded-2xl border border-stone-200 bg-white p-6 shadow-sm">
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col text-xs">
              <span className="text-stone-500">Email</span>
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="someone@shulkinfamilypartners.com"
                className="rounded border border-stone-300 px-2 py-1.5 text-sm focus:border-stone-500 focus:outline-none"
              />
            </label>
            <label className="flex flex-col text-xs">
              <span className="text-stone-500">Display name</span>
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="rounded border border-stone-300 px-2 py-1.5 text-sm focus:border-stone-500 focus:outline-none"
              />
            </label>
          </div>

          <label className="flex flex-col text-xs">
            <span className="text-stone-500">Role (sets sensible defaults — tweak below)</span>
            <select
              value={userRole}
              onChange={(e) => applyRoleDefaults(e.target.value as Role)}
              className="rounded border border-stone-300 px-2 py-1.5 text-sm"
            >
              {ROLES.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </select>
          </label>

          <Section title="Tile groups visible on home base">
            <CheckGrid
              options={DEFAULT_HOME_LAYOUT.groups.map((g) => ({ id: g.id, label: g.title }))}
              selected={allowedTileGroupIds}
              onChange={setAllowedTileGroupIds}
              emptyHint="Leaving every box unchecked is treated as 'role defaults'."
            />
          </Section>

          <Section title="Sidebar items">
            <CheckGrid
              options={DEFAULT_HOME_LAYOUT.sidebar.flatMap((s) =>
                s.items.map((it) => ({ id: it.id, label: it.label })),
              )}
              selected={allowedSidebarItemIds}
              onChange={setAllowedSidebarItemIds}
              emptyHint="Leaving every box unchecked is treated as 'role defaults'."
            />
          </Section>

          <Section title="Document categories">
            <CheckGrid
              options={CATEGORIES.map((c) => ({ id: c, label: c.replace(/_/g, ' ') }))}
              selected={allowedCategories}
              onChange={(v) => setAllowedCategories(v as Category[])}
              emptyHint="Default = role-based category map."
            />
          </Section>

          <Section title="Apps / topics they can use">
            <CheckGrid
              options={TOPICS}
              selected={allowedTopicIds}
              onChange={setAllowedTopicIds}
            />
          </Section>

          {error && (
            <div className="rounded border border-[#9C4A3A] bg-[#9C4A3A]/10 px-3 py-2 text-xs text-[#9C4A3A]">
              {error}
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={busy || !email}
              className="rounded bg-[color:var(--sfp-primary)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {busy ? 'Inviting…' : 'Invite user'}
            </button>
            <span className="text-[11px] text-stone-500">
              They'll appear in <strong>Users</strong> immediately and can sign in once they set their password.
            </span>
          </div>
        </form>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-[10px] uppercase tracking-wider text-stone-500">{title}</div>
      {children}
    </div>
  );
}

function CheckGrid({
  options,
  selected,
  onChange,
  emptyHint,
}: {
  options: Array<{ id: string; label: string }>;
  selected: string[];
  onChange: (v: string[]) => void;
  emptyHint?: string;
}) {
  const set = new Set(selected);
  return (
    <div>
      <div className="grid grid-cols-2 gap-1.5 md:grid-cols-3">
        {options.map((o) => (
          <label key={o.id} className="flex items-center gap-2 rounded border border-stone-200 bg-stone-50 px-2 py-1 text-xs">
            <input
              type="checkbox"
              checked={set.has(o.id)}
              onChange={(e) => {
                const next = new Set(selected);
                if (e.target.checked) next.add(o.id);
                else next.delete(o.id);
                onChange([...next]);
              }}
            />
            <span className="truncate">{o.label}</span>
          </label>
        ))}
      </div>
      {emptyHint && selected.length === 0 && (
        <div className="mt-1 text-[10px] italic text-stone-400">{emptyHint}</div>
      )}
    </div>
  );
}
